import { LogOut, User, Menu } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCongregation } from '../../contexts/CongregationContext';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from './Layout';
import NotificationDropdown from '../notifications/NotificationDropdown';
import Logo from '../Logo';

export default function Header() {
  const { profile, signOut } = useAuth();
  const { active } = useCongregation();
  const { toggle } = useSidebar();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getRoleBadge = () => {
    switch (profile?.role) {
      case 'superadmin':
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: '#F9E7B5', color: '#B08C1F' }}>Super Admin</span>;
      case 'beheerder':
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: '#F9E7B5', color: '#B08C1F' }}>Beheerder</span>;
      case 'groepsbeheerder':
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: '#F9E7B5', color: '#B08C1F' }}>Groepsbeheerder</span>;
      case 'vrijwilliger':
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: '#CFF4EE', color: '#1F7A52' }}>Vrijwilliger</span>;
      default:
        return <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: '#CFF4EE', color: '#1F7A52' }}>Vrijwilliger</span>;
    }
  };

  return (
    <header className="h-16 flex items-center justify-between px-4 sm:px-6 border-b bg-white border-[#E5E7EB]" style={{ boxShadow: '0 1px 4px rgba(17,24,39,0.06)' }}>
      <div className="flex items-center gap-3">
        <button
          onClick={toggle}
          className="lg:hidden p-2 -ml-2 rounded-lg transition-colors hover:bg-[#F9FAFB]"
          style={{ color: '#6B7280' }}
        >
          <Menu className="w-6 h-6" />
        </button>

        <div className="lg:hidden flex items-center gap-2">
          <Logo size="sm" />
          <span
            className="text-base font-semibold hidden sm:block"
            style={{ fontFamily: 'Inter, system-ui, sans-serif', color: '#2F313F' }}
          >
            kerkplanner
          </span>
        </div>

        <h1
          className="hidden lg:block text-lg font-semibold"
          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: '#2F313F' }}
        >
          Welkom, {profile?.full_name || 'Vrijwilliger'}
        </h1>
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        <NotificationDropdown />

        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 rounded-xl transition-colors hover:bg-[#F9FAFB]"
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold text-white"
              style={{ backgroundColor: 'var(--color-primary)' }}
            >
              {profile?.full_name?.charAt(0)?.toUpperCase() || 'V'}
            </div>
            <div className="text-left hidden md:block">
              <p className="text-sm font-medium" style={{ color: '#2F313F' }}>{profile?.full_name || 'Gebruiker'}</p>
              <p className="text-xs truncate max-w-[150px]" style={{ color: '#6B7280' }}>{profile?.email}</p>
            </div>
          </button>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-64 rounded-xl py-2 z-50 border animate-scale-in bg-white border-[#E5E7EB]" style={{ boxShadow: '0 4px 14px rgba(17,24,39,0.08)' }}>
              <div className="px-4 py-3 border-b border-[#E5E7EB]">
                <p className="text-sm font-semibold" style={{ color: '#2F313F' }}>{profile?.full_name}</p>
                <p className="text-xs truncate" style={{ color: '#6B7280' }}>{profile?.email}</p>
                {active?.short_name && (
                  <p className="text-xs mt-0.5" style={{ color: '#9CA3AF' }}>{active.short_name}</p>
                )}
                <div className="mt-2">{getRoleBadge()}</div>
              </div>
              <button
                onClick={() => { setShowDropdown(false); navigate('/instellingen'); }}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors hover:bg-[#F9FAFB]"
                style={{ color: '#4B5563' }}
              >
                <User className="w-4 h-4" />
                Profiel bewerken
              </button>
              <button
                onClick={signOut}
                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors hover:bg-[#FCE8E6]"
                style={{ color: '#C85A50' }}
              >
                <LogOut className="w-4 h-4" />
                Uitloggen
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
